import React from 'react'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'
import { t } from 'ttag'

import { useControlledForm, useFormApiError } from 'hooks'
import { mapInternalStateToPayload } from '../actions'
import {
  QUESTIONS_GROUP_FIELD,
  QUESTION_STATUSES,
  getDefaultValues,
  getValues,
} from '../model'
import { getSchema } from '../schema'

// NOTE: hook that exposes edit form state and its handlers
// for other local hooks and interview templates creator
// components
export const useEditFormState = (state, handlers) => {
  const { activeSection, currentStepIndex, templateUrl, isInsertedCategory } =
    state
  const {
    handleChangeMode,
    handleChangeStep,
    handleDeleteTemplateQuestion,
    handlePatchTemplateQuestion,
    handlePatchTemplateSection,
    handlePostTemplateQuestion,
    handlePostTemplateSection,
    handleSetResponse,
    handleShowSnackbar,
  } = handlers

  const [apiError, setApiError] = React.useState(null)

  const { id, questions = [], scoreable, title, url } = activeSection

  const defaultValues = getDefaultValues()
  const schema = getSchema()
  const values = getValues({ questions, scoreable, title })

  const handleNewFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({ ...data })

      const section = await handlePostTemplateSection({
        interviewTemplate: templateUrl,
        key: R.toLower(payload?.title),
        order: currentStepIndex,
        scoreable: payload?.scoreable,
        title: payload?.title,
      })

      if (!R.isNil(section?.error)) {
        setApiError({ message: section?.error, type: section?.meta.HTTPstatus })
      } else {
        const questions = await handlePostTemplateQuestion({
          questions: R.addIndex(R.map)(
            (question, index) => R.assoc('order', R.inc(index), question),
            payload?.questions,
          ),
          sectionUrl: section?.response?.url,
        })

        handleSetResponse(prevState => {
          handleChangeStep(section?.response?.key)

          return R.over(
            R.lensPath(['sections', currentStepIndex]),
            R.pipe(
              R.mergeLeft(section?.response),
              R.assoc(
                ['questions'],
                R.map(question => question?.response, questions),
              ),
            ),
            prevState,
          )
        })
        handleChangeMode()
        handleShowSnackbar({
          message: t`Interview template category was successfully created.`,
          type: 'success',
        })
      }
    },
    [
      currentStepIndex,
      templateUrl,
      handleChangeMode,
      handleChangeStep,
      handlePostTemplateQuestion,
      handlePostTemplateSection,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleEditFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({ ...data })

      const questions = R.pipe(
        R.addIndex(R.map)((question, index) => {
          const count = R.pipe(
            R.slice(0, index),
            R.filter(R.propEq('status', QUESTION_STATUSES.DELETED)),
            R.length,
          )(payload?.questions)

          return R.assoc(
            'order',
            R.equals(question.status, QUESTION_STATUSES.DELETED)
              ? index
              : R.inc(index) - count,
            question,
          )
        }),
        R.groupBy(({ status }) =>
          R.equals(status, QUESTION_STATUSES.DELETED)
            ? 'deleted'
            : R.equals(status, QUESTION_STATUSES.FETCHED)
              ? 'fetched'
              : 'inserted',
        ),
      )(payload?.questions)

      await Promise.all([
        handleDeleteTemplateQuestion({ questions: questions?.deleted }),
        handlePatchTemplateQuestion({ questions: questions?.fetched }),
        handlePostTemplateQuestion({
          questions: questions?.inserted,
          sectionUrl: url,
        }),
      ])

      const section = await handlePatchTemplateSection({
        scoreable: payload?.scoreable,
        title: payload?.title,
        uuid: id,
      })

      if (!R.isNil(section?.error)) {
        setApiError({ message: section?.error, type: section?.meta.HTTPstatus })
      } else {
        handleSetResponse(prevState => {
          handleChangeStep(section?.response?.key)

          return R.over(
            R.lensPath(['sections', currentStepIndex]),
            R.mergeLeft(section?.response),
            prevState,
          )
        })
        handleChangeMode()
        handleShowSnackbar({
          message: t`Interview template category was successfully updated.`,
          type: 'success',
        })
      }
    },
    [
      currentStepIndex,
      id,
      url,
      handleChangeMode,
      handleChangeStep,
      handleDeleteTemplateQuestion,
      handlePatchTemplateQuestion,
      handlePatchTemplateSection,
      handlePostTemplateQuestion,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleFormData = React.useCallback(
    async data => {
      isInsertedCategory
        ? await handleNewFormData(data)
        : await handleEditFormData(data)
    },
    [handleEditFormData, handleNewFormData, isInsertedCategory],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    watch,
  } = useControlledForm({
    values: values ? values : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: QUESTIONS_GROUP_FIELD,
  })

  const hasNoQuestions = R.lte(
    R.count(
      ({ status }) => !R.equals(status, QUESTION_STATUSES.DELETED),
      fields,
    ),
    1,
  )

  useFormApiError({ apiError }, handleFormApiError)

  return {
    state: { edit: { control, fields, formState }, hasNoQuestions },
    handlers: {
      handleAppendQuestion: append,
      handleRemoveQuestion: remove,
      handleUpdateQuestion: update,
      handleFormReset,
      handleFormSubmit,
      watch,
    },
  }
}
